import { SessionStorage } from '../browserStorage'
import { SessionStorageKeys } from '../constants'

export const SUPPORTED_LOCALES = ['en', 'pt-BR'] as const
export type Locale = typeof SUPPORTED_LOCALES[number]
export const DEFAULT_LOCALE: Locale = 'en'

const isSupportedLocale = (value: unknown): value is Locale =>
  typeof value === 'string' &&
  (SUPPORTED_LOCALES as readonly string[]).includes(value)

/**
 * Resolve a single BCP-47 language tag (as found in `Accept-Language` /
 * `navigator.languages`) to a supported locale. An exact match wins; failing
 * that we match on the primary subtag so `pt`, `pt-BR` and `pt-PT` all resolve
 * to `pt-BR`, and `en-US`/`en-GB` resolve to `en`. Returns `null` when nothing
 * matches.
 */
const matchSupportedLocale = (tag: string): Locale | null => {
  const normalized = tag.trim().toLowerCase()
  if (!normalized) return null

  const exact = SUPPORTED_LOCALES.find((l) => l.toLowerCase() === normalized)
  if (exact) return exact

  const primary = normalized.split('-')[0]
  const byPrimary = SUPPORTED_LOCALES.find(
    (l) => l.toLowerCase().split('-')[0] === primary
  )
  return byPrimary ?? null
}

/**
 * The browser's ordered language preferences — the same list the browser uses
 * to build the `Accept-Language` header it sends with every request. Falls back
 * to the single `navigator.language` on older browsers.
 */
const getBrowserLanguages = (): string[] => {
  try {
    if (Array.isArray(navigator.languages) && navigator.languages.length) {
      return Array.from(navigator.languages)
    }
    return navigator.language ? [navigator.language] : []
  } catch {
    return []
  }
}

/**
 * Resolve the active locale for the current session. Precedence:
 *   1. `?locale=` (or `?lang=`) on the current URL
 *   2. a locale previously persisted to session storage
 *   3. the browser's `Accept-Language` preference (`navigator.languages`)
 *   4. DEFAULT_LOCALE ('en')
 *
 * A locale supplied in the query string is persisted so it survives the
 * in-flow navigations (and Auth0 round-trips) of the onboarding funnel.
 * Unrecognised values are ignored and fall through to the next source.
 */
export const getActiveLocale = (): Locale => {
  let fromQuery: string | null = null
  try {
    const params = new URLSearchParams(window.location.search)
    fromQuery = params.get('locale') || params.get('lang')
  } catch {
    fromQuery = null
  }

  if (isSupportedLocale(fromQuery)) {
    SessionStorage.set(SessionStorageKeys.locale, fromQuery)
    return fromQuery
  }

  const fromStorage = SessionStorage.get(SessionStorageKeys.locale)
  if (isSupportedLocale(fromStorage)) {
    return fromStorage
  }

  const fromBrowser = getBrowserLanguages()
    .map(matchSupportedLocale)
    .find((locale): locale is Locale => locale !== null)
  if (fromBrowser) {
    return fromBrowser
  }

  return DEFAULT_LOCALE
}

type LocaleModelMap<T> = { en: T; [locale: string]: unknown }

/**
 * Pick the string model matching the active locale, falling back to the
 * English model when the active locale has no entry.
 *
 *   const model = localize({ en: enModel, 'pt-BR': ptBRModel })
 *
 * The returned value is typed from the `en` model, so callers keep full
 * type-safety against the canonical English shape.
 */
export const localize = <T>(models: LocaleModelMap<T>): T => {
  const locale = getActiveLocale()
  const picked = locale === DEFAULT_LOCALE ? models.en : models[locale]
  return (picked as T) ?? models.en
}
